import React, { useState, useEffect, useMemo } from "react";
import { getSummaryReport, getPipelineReport, getChannelsReport, getEditorsReport, getGapsReport } from "../lib/api";
import { toast } from "sonner";
import { ChartBar, TrendUp, Warning, Users, VideoCamera } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#22C55E"];
const chartXAxisTick = { fill: "#718096", fontSize: 12 };
const chartYAxisTick = { fill: "#A0AEC0", fontSize: 11 };
const chartYAxisTickSm = { fill: "#A0AEC0", fontSize: 10 };

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [summaryData, setSummaryData] = useState(null);
  const [pipelineData, setPipelineData] = useState(null);
  const [channelsData, setChannelsData] = useState(null);
  const [editorsData, setEditorsData] = useState(null);
  const [gapsData, setGapsData] = useState(null);

  const contentTypeData = useMemo(() => [
    { name: "Long", value: summaryData?.content_type?.long || 0 },
    { name: "Short", value: summaryData?.content_type?.short || 0 },
  ], [summaryData?.content_type?.long, summaryData?.content_type?.short]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summary, pipeline, channels, editors, gaps] = await Promise.all([
        getSummaryReport(period),
        getPipelineReport(),
        getChannelsReport(),
        getEditorsReport(),
        getGapsReport()
      ]);
      setSummaryData(summary.data);
      setPipelineData(pipeline.data);
      setChannelsData(channels.data);
      setEditorsData(editors.data);
      setGapsData(gaps.data);
    } catch (err) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [period]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1A1A2E] border border-white/10 rounded-lg px-3 py-2 shadow-lg">
          <p className="text-white text-sm">{label}</p>
          <p className="text-blue-400 text-sm">{payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <div className="space-y-6">{[1, 2, 3].map(i => <div key={i} className="surface-card p-6"><div className="skeleton h-64 w-full" /></div>)}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold text-white">Reports & Analytics</h1>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A2E] border-white/10">
            <SelectItem value="week" className="text-white">This Week</SelectItem>
            <SelectItem value="month" className="text-white">This Month</SelectItem>
            <SelectItem value="quarter" className="text-white">This Quarter</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="summary" className="space-y-6">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="summary" className="data-[state=active]:bg-blue-600">Summary</TabsTrigger>
          <TabsTrigger value="pipeline" className="data-[state=active]:bg-blue-600">Pipeline</TabsTrigger>
          <TabsTrigger value="channels" className="data-[state=active]:bg-blue-600">Channels</TabsTrigger>
          <TabsTrigger value="editors" className="data-[state=active]:bg-blue-600">Editors</TabsTrigger>
          <TabsTrigger value="gaps" className="data-[state=active]:bg-blue-600">Gaps</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Published by Channel */}
            <div className="surface-card p-6">
              <h3 className="font-heading text-lg font-medium text-white mb-4">Published by Channel</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summaryData?.published_by_channel || []} layout="vertical">
                    <XAxis type="number" tick={chartXAxisTick} />
                    <YAxis type="category" dataKey="channel" tick={chartYAxisTick} width={120} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {(summaryData?.published_by_channel || []).map((entry, idx) => (
                        <Cell key={entry.channel || idx} fill={entry.color || COLORS[idx % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Content Type */}
            <div className="surface-card p-6">
              <h3 className="font-heading text-lg font-medium text-white mb-4">Content Type Distribution</h3>
              <div className="h-64 flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={contentTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      <Cell fill="#3B82F6" />
                      <Cell fill="#8B5CF6" />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Top Editors */}
          <div className="surface-card p-6">
            <h3 className="font-heading text-lg font-medium text-white mb-4">Most Productive Editors</h3>
            <div className="space-y-3">
              {(summaryData?.editor_productivity || []).map((editor, idx) => (
                <div key={editor.name || idx} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02]">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-500">#{idx + 1}</span>
                    <span className="text-white">{editor.name}</span>
                  </div>
                  <span className="text-blue-400 font-medium">{editor.count} videos</span>
                </div>
              ))}
              {(summaryData?.editor_productivity || []).length === 0 && (
                <p className="text-gray-500 text-center py-4">No data available</p>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pipeline" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="stat-card">
              <p className="text-sm text-gray-400">Videos Without Editor</p>
              <p className="text-3xl font-heading font-semibold text-amber-400">{pipelineData?.videos_no_editor || 0}</p>
            </div>
            <div className="stat-card">
              <p className="text-sm text-gray-400">Videos Without Schedule</p>
              <p className="text-3xl font-heading font-semibold text-amber-400">{pipelineData?.videos_no_schedule || 0}</p>
            </div>
          </div>

          <div className="surface-card p-6">
            <h3 className="font-heading text-lg font-medium text-white mb-4">Bottleneck Analysis</h3>
            <div className="space-y-4">
              {(pipelineData?.bottlenecks || []).map((stage) => (
                <div key={stage.stage} className="p-4 rounded-lg bg-white/[0.02]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white capitalize font-medium">{stage.stage}</span>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-400">{stage.total} total</span>
                      {stage.stuck_count > 0 && (
                        <span className="text-red-400 flex items-center gap-1">
                          <Warning size={14} /> {stage.stuck_count} stuck 7+ days
                        </span>
                      )}
                    </div>
                  </div>
                  {stage.stuck_videos?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {stage.stuck_videos.slice(0, 3).map(v => (
                        <p key={v.id} className="text-xs text-gray-500">
                          • {v.title} ({v.days_stuck} days)
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="channels">
          <div className="surface-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0B0C10] text-[#A0AEC0] text-sm uppercase tracking-wider">
                  <th className="text-left p-4 font-medium">Channel</th>
                  <th className="text-left p-4 font-medium">Published</th>
                  <th className="text-left p-4 font-medium">Shorts</th>
                  <th className="text-left p-4 font-medium">Pipeline</th>
                  <th className="text-left p-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(channelsData || []).map((ch, idx) => (
                  <tr key={ch.id} className={`table-row ${idx % 2 === 1 ? "table-row-even" : ""}`}>
                    <td className="p-4 text-white font-medium">{ch.name}</td>
                    <td className="p-4 text-green-400">{ch.published_this_month}</td>
                    <td className="p-4 text-purple-400">{ch.shorts_published}</td>
                    <td className="p-4 text-gray-300">{ch.pipeline_count}</td>
                    <td className="p-4"><span className={`badge ${ch.status === "active" ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}`}>{ch.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="editors">
          <div className="surface-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0B0C10] text-[#A0AEC0] text-sm uppercase tracking-wider">
                  <th className="text-left p-4 font-medium">Editor</th>
                  <th className="text-left p-4 font-medium">Completed</th>
                  <th className="text-left p-4 font-medium">Current Load</th>
                  <th className="text-left p-4 font-medium">Availability</th>
                  <th className="text-left p-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(editorsData || []).map((ed, idx) => (
                  <tr key={ed.id} className={`table-row ${idx % 2 === 1 ? "table-row-even" : ""}`}>
                    <td className="p-4 text-white font-medium">{ed.name}</td>
                    <td className="p-4 text-green-400">{ed.completed_this_month}</td>
                    <td className="p-4 text-gray-300">{ed.current_load}/{ed.max_capacity}</td>
                    <td className="p-4"><span className={ed.availability > 0 ? "text-green-400" : "text-red-400"}>{ed.availability}</span></td>
                    <td className="p-4"><span className={`badge ${ed.status === "active" ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}`}>{ed.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="gaps" className="space-y-4">
          {(gapsData || []).length === 0 ? (
            <div className="surface-card p-8 text-center">
              <TrendUp size={48} className="text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">All Caught Up!</h3>
              <p className="text-gray-400">No content gaps detected for the next 2 weeks</p>
            </div>
          ) : (
            (gapsData || []).map((gap) => (
              <div key={gap.channel_id} className="surface-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-medium">{gap.channel_name}</h3>
                  <span className="text-sm text-gray-400">{gap.posting_schedule}</span>
                </div>
                <p className="text-amber-400 text-sm mb-2">
                  <Warning size={14} className="inline mr-1" />
                  {gap.gap_days?.length || 0} days without scheduled content
                </p>
                <div className="flex flex-wrap gap-2">
                  {gap.gap_days?.slice(0, 7).map(date => (
                    <span key={date} className="px-2 py-1 rounded bg-amber-500/10 text-amber-400 text-xs">{date}</span>
                  ))}
                </div>
              </div>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
